// Sync's bookkeeping on the device: what happened to each change it sent
// (reached CRM, conflict, failed, nothing left to send). Used only by
// outbox.ts. Each step that touches a record and its outbox entry does both in
// one transaction (device-store.ts).
import { withUploadedPhoto, type PhotoListEntry } from './area-photos';
import type { Direction } from './compass';
import { db, notifyChange, queue, type OutboxEntry } from './device-store';

/** A record reached CRM. If it was edited again while sending, it stays pending and re-queued. */
export async function markRecordSent(entry: OutboxEntry, sentRevision: number, etag: string): Promise<void> {
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  const local = await tx.objectStore('records').get(entry.targetId);
  await tx.objectStore('outbox').delete(entry.seq!);
  if (local) {
    const movedOn = local.revision !== sentRevision;
    await tx.objectStore('records').put({ ...local, etag, state: movedOn ? 'pending' : 'synced' });
    if (movedOn) await queue(tx, { kind: 'record', targetId: local.id, jobId: local.jobId });
  }
  await tx.done;
  notifyChange();
}

/**
 * CRM moved a record's etag itself (a measure re-totalled after its area
 * saved). Only columns the device never writes changed, so take the etag up
 * rather than call the device's next save of it a conflict.
 */
export async function adoptEtag(recordId: string, etag: string): Promise<void> {
  const tx = (await db()).transaction(['records'], 'readwrite');
  const local = await tx.objectStore('records').get(recordId);
  if (local) await tx.objectStore('records').put({ ...local, etag });
  await tx.done;
}

/**
 * Another record's new etag from a save that changed it in CRM (an area whose
 * photo folder a building change renamed): taken up only while this device's
 * copy has nothing waiting. A copy with an edit waiting, or in conflict, keeps
 * its etag, so its save still meets CRM's newer copy and is shown as a conflict
 * rather than sent over it.
 */
export async function adoptEtagIfSynced(recordId: string, etag: string): Promise<void> {
  const tx = (await db()).transaction(['records'], 'readwrite');
  const local = await tx.objectStore('records').get(recordId);
  if (local?.state === 'synced') await tx.objectStore('records').put({ ...local, etag });
  await tx.done;
}

/**
 * CRM refused the change (403): the job doesn't open for this user, or their
 * role doesn't allow it — or its record is no longer in CRM (404). Sending it again would only be refused again, so it
 * is set aside — kept on the device with the reason, out of the outbox — and
 * sync carries on with the rest (refused.ts: try again, or discard).
 */
export async function markRefused(entry: OutboxEntry, reason: string): Promise<void> {
  const tx = (await db()).transaction(['records', 'photos', 'outbox'], 'readwrite');
  await tx.objectStore('outbox').delete(entry.seq!);
  if (entry.kind === 'record') {
    const local = await tx.objectStore('records').get(entry.targetId);
    if (local) await tx.objectStore('records').put({ ...local, state: 'conflict', refusedReason: reason });
  } else if (entry.kind === 'photo-remove') {
    // Not allowed to remove it: the photo comes back as CRM has it (its device copy went when it was removed).
    const local = await tx.objectStore('records').get(entry.targetId);
    if (local) await tx.objectStore('records').put({ ...local, state: 'synced', removing: undefined });
  } else {
    const photo = await tx.objectStore('photos').get(entry.targetId);
    if (photo) await tx.objectStore('photos').put({ ...photo, refusedReason: reason });
  }
  await tx.done;
  notifyChange();
}

/** CRM had a newer version: keep the device's copy, flag it, stop sending it. */
export async function markRecordConflict(entry: OutboxEntry): Promise<void> {
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  const local = await tx.objectStore('records').get(entry.targetId);
  await tx.objectStore('outbox').delete(entry.seq!);
  if (local) await tx.objectStore('records').put({ ...local, state: 'conflict' });
  await tx.done;
  notifyChange();
}

/**
 * Opens the transaction a sent photo's bookkeeping runs in: its outbox entry
 * is done, and the full-size copy is on SharePoint now, so the device keeps
 * only the thumbnail. The caller finishes the transaction.
 */
async function photoSent(entry: OutboxEntry) {
  const tx = (await db()).transaction(['photos', 'records', 'outbox'], 'readwrite');
  const photo = await tx.objectStore('photos').get(entry.targetId);
  await tx.objectStore('outbox').delete(entry.seq!);
  if (photo) await tx.objectStore('photos').put({ ...photo, image: null, state: 'uploaded' });
  return { tx, photo };
}

/**
 * A photo reached SharePoint and its row: the row's file columns (the
 * server's, never sent from the device) are filled in on the device's copy and
 * its etag taken up, so the photo's folder opens straight away and its next
 * change (a pin) isn't a conflict with the upload. A conflicted row keeps its etag.
 */
export async function markPhotoSentToRow(entry: OutboxEntry, rowEtag: string, webUrl: string | undefined): Promise<void> {
  const { tx } = await photoSent(entry);
  const row = await tx.objectStore('records').get(entry.targetId);
  if (row) {
    const fields = { ...row.fields, hig_uploadedon: new Date().toISOString(), hig_weburl: webUrl ?? row.fields.hig_weburl ?? null, hig_filemissingsince: null };
    await tx.objectStore('records').put({ ...row, fields, etag: row.state === 'conflict' ? row.etag : rowEtag });
  }
  await tx.done;
  notifyChange();
}

/**
 * A photo (or its new direction) reached SharePoint and its area's older list
 * (a photo with no row), which moved the area's etag. A direction changed
 * while it was sending goes again. `listed` is a new upload's entry on the
 * area's photo list, kept on the device's copy so the photo's SharePoint
 * folder opens straight away.
 */
export async function markPhotoSent(entry: OutboxEntry, areaEtag: string, sentDirection: Direction | null, listed?: PhotoListEntry): Promise<void> {
  const { tx, photo } = await photoSent(entry);
  if (photo) {
    // A conflicted area keeps its old etag: taking CRM's new one would let its next edit go over the other person's save unasked (#394).
    const area = await tx.objectStore('records').get(photo.areaId);
    if (area) {
      // hig_photos is the server's column, never sent from the device, so the copy is safe to extend even mid-conflict.
      const fields = listed ? { ...area.fields, hig_photos: withUploadedPhoto(area.fields.hig_photos, listed) } : area.fields;
      await tx.objectStore('records').put({ ...area, fields, etag: area.state === 'conflict' ? area.etag : areaEtag });
    }
    if ((photo.direction ?? null) !== sentDirection) await queue(tx, { kind: 'photo-meta', targetId: photo.id, jobId: photo.jobId });
  }
  await tx.done;
  notifyChange();
}

export async function markAttemptFailed(entry: OutboxEntry, error: string): Promise<void> {
  const database = await db();
  await database.put('outbox', { ...entry, attempts: entry.attempts + 1, lastError: error });
  notifyChange();
}

export async function markJobSynced(jobId: string): Promise<void> {
  const database = await db();
  const job = await database.get('jobs', jobId);
  if (job) await database.put('jobs', { ...job, lastSyncedAt: new Date().toISOString() });
  notifyChange();
}

/** One more try used up, without a stored error: a note that a job was opened isn't a change, so it never shows as a failed sync. */
export async function countAttempt(entry: OutboxEntry): Promise<void> {
  await (await db()).put('outbox', { ...entry, attempts: entry.attempts + 1 });
}

/** A sent entry whose target is gone from the device (nothing left to send). */
export async function dropEntry(entry: OutboxEntry): Promise<void> {
  await (await db()).delete('outbox', entry.seq!);
  notifyChange();
}
